import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate} from 'react-router-dom';
import {useSearch} from '../contexts/SearchContext';
import {Checkbox} from 'primereact/checkbox';
import './SearchWidget.scss';

const SearchWidget: React.FC = () => {
  const {t} = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { searchCriteria, setSearchTitle, setSearchOptions } = useSearch();
  const [searchValue, setSearchValue] = useState('');
  const [exactSearch, setExactSearch] = useState(false);
  const [onlyTitles, setOnlyTitles] = useState(false);

  const searchVisiblePaths = ['/gallery', '/home', '/legacy-home', '/browse-collections', '/locations'];

  const isSearchVisible = searchVisiblePaths.some(path =>
    location.pathname === path || location.pathname.startsWith(path + '/')
  );

  // Sync search value with context when searchTerms changes from other sources
  useEffect(() => {
    setSearchValue(searchCriteria.searchTerms || '');
  }, [searchCriteria.searchTerms]);

  // Sync checkbox options with context when they change from other sources
  useEffect(() => {
    setExactSearch(searchCriteria.exactSearch || false);
    setOnlyTitles(searchCriteria.onlyTitles || false);
  }, [searchCriteria.exactSearch, searchCriteria.onlyTitles]);

  const handleSearchClick = () => {
    setSearchTitle(searchValue);
    if (location.pathname !== '/gallery') {
      navigate('/gallery');
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearchClick();
    }
  };

  const handleClearSearch = () => {
    setSearchValue('');
    setSearchTitle('');
  };

  const handleExactSearchChange = (checked: boolean) => {
    setExactSearch(checked);
    setSearchOptions(checked, onlyTitles);
  };

  const handleOnlyTitlesChange = (checked: boolean) => {
    setOnlyTitles(checked);
    setSearchOptions(exactSearch, checked);
  };

  const hasActiveSearch = !!searchCriteria.searchTerms;

  return (
    <div className={`search-widget max-w-3xl mx-auto ${!isSearchVisible ? 'hidden' : ''}`}>
      <div className="search-input relative">
        <input
          type="text"
          placeholder={t('search.placeholder')}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onKeyPress={handleKeyPress}
          className={`w-full py-2 px-12 ${hasActiveSearch ? 'pr-20' : 'pr-16'} focus:outline-none ${hasActiveSearch
              ? 'bg-white text-gray-900 hover:bg-gray-100'
              : 'bg-gray-900 text-white hover:bg-gray-800'
          }`}
        />
        <i
          className={`absolute left-4 top-1/2 transform -translate-y-1/2 fa fa-magnifying-glass ${hasActiveSearch ? 'text-gray-600' : 'text-gray-400'
          }`}></i>
        {hasActiveSearch && (
          <button
            onClick={handleClearSearch}
            className="absolute right-[5.5rem] top-1/2 transform -translate-y-1/2 p-2 text-gray-600 hover:text-gray-800"
            title={t('search.clear')}
          >
            <i className="fa fa-xmark"></i>
          </button>
        )}
        <button
          onClick={handleSearchClick}
          className={`absolute right-0 py-2 leading-6 top-1/2 transform -translate-y-1/2 px-4 text-md ${hasActiveSearch
              ? 'bg-gray-300 hover:bg-gray-400 text-black'
              : 'bg-gray-700 hover:bg-gray-600 text-white'
          }`}
        >
          {t('search.button')}
        </button>
      </div>
      <div
        className={`search-options flex items-center gap-4 text-[13px] px-4 py-1 rounded-es-md rounded-ee-md relative ${hasActiveSearch
          ? 'text-gray-600 bg-gray-100'
          : 'text-gray-400 bg-black'
        }`}>
        <div className="flex items-center gap-1.5">
          <Checkbox
            inputId="exactSearch"
            checked={exactSearch}
            onChange={(e) => handleExactSearchChange(e.checked || false)}
          />
          <label
            htmlFor="exactSearch"
            className="cursor-pointer text-[13px]"
            title={t('search.tooltips.exact')}
          >
            {t('search.options.exact')}
          </label>
        </div>
        <div className="flex items-center gap-1.5">
          <Checkbox
            inputId="onlyTitles"
            checked={onlyTitles}
            onChange={(e) => handleOnlyTitlesChange(e.checked || false)}
          />
          <label
            htmlFor="onlyTitles"
            className="cursor-pointer text-[13px]"
            title={t('search.tooltips.titles')}
          >
            {t('search.options.titles')}
          </label>
        </div>
      </div>
    </div>
  );
};

export default SearchWidget;
